import { FC, useRef } from 'react'
import { ModalContent, ModalBody, Divider, Card, useDisclosure } from '@nextui-org/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { InsertTable } from '../common/Table'
import type { TankStockValues } from '@/types/view'
import stockColumns from './stockColumns'
import { insertDataInGradeUpdate } from '../../utils/selectTank/common'
import useHomeStockStore from '../../store/home/useHomeStockStore'
import { useGroupStore } from '../../store/group/useGroupStore'
import { BaseModal, BaseModalHeader } from '../common/Modal'
import { InitialInfoButton } from './InitialInfoButton'
import { EventType } from '../../components/common/Input'
import { ModalText } from '../common/Text'
import { AddGroup } from '../addgroup/AddGroup'
import { getGroups } from '../../api/group/group.service'

export interface InitialInfoProps {
  onOpenChange: () => void
  isOpen: boolean
  isBack: () => void
}

export const InitialInfo: FC<InitialInfoProps> = ({ onOpenChange, isOpen, isBack }) => {
  const {
    tanksValues,
    setTanksValues,
    tanksInfoBorderColor,
    requiredError,
    typeError,
    groupSelections,
    setGroupForRow,
    setBatchInputForRow,
    setPendingCreateGroupRowIndex,
    applyNewGroupToRow,
    validationErrors,
  } = useHomeStockStore()

  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(tanksValues.length).fill(null))

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: getGroups,
    enabled: isOpen,
  })

  const queryClient = useQueryClient()
  const addGroupDisclosure = useDisclosure()

  const insertData = (event: EventType) => {
    insertDataInGradeUpdate<TankStockValues>(event, tanksValues, setTanksValues)
  }

  const handleBatchInputChange = (rowIndex: number, value: string) => {
    setBatchInputForRow(rowIndex, value)
  }

  const handleGroupChange = (rowIndex: number, groupId: string | null, groupName: string | null) => {
    setGroupForRow(rowIndex, groupId, groupName)
  }

  const handleGroupCreateNew = (rowIndex: number) => {
    setPendingCreateGroupRowIndex(rowIndex)
    addGroupDisclosure.onOpen()
  }

  const handleAddGroupClose = () => {
    // Invalidate React Query cache so dropdown refetches the latest groups
    queryClient.invalidateQueries({ queryKey: ['groups'] })

    // Find newly created group by comparing IDs between store and query cache
    const latestGroups = useGroupStore.getState().groups
    const pendingRow = useHomeStockStore.getState().pendingCreateGroupRowIndex

    if (pendingRow !== null) {
      const existingIds = new Set(groups.map((g) => g.id))
      const newGroup = latestGroups.find((g) => !existingIds.has(g.id))
      if (newGroup) {
        applyNewGroupToRow(newGroup.id, newGroup.name)
      } else {
        setPendingCreateGroupRowIndex(null)
      }
    }
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size={'3xl'}
      onBrowserBack={isBack}
    >
      <ModalContent className="overflow-x-hidden">
        {(onClose) => (
          <>
            <BaseModalHeader title="Stock" description={ModalText('InsertTable')} />
            <ModalBody className="gap-0 min-h-[25rem] pl-6 sm:pl-10 pr-6 sm:pr-10 overflow-x-hidden">
              <Divider className="my-4" style={{ margin: '1px' }} />
              <Card className={`mt-3 ${tanksInfoBorderColor} border-opacity-60 min-h-[20rem]`}>
                <InsertTable
                  tanksValues={tanksValues}
                  inputRefs={inputRefs}
                  columns={stockColumns}
                  insertData={insertData}
                  groups={groups}
                  groupSelections={groupSelections}
                  onGroupChange={handleGroupChange}
                  onGroupCreateNew={handleGroupCreateNew}
                  onBatchInputChange={handleBatchInputChange}
                />
              </Card>
              {typeError ? (
                <p className="m-0 text-sm font-semibold text-tdplmaroon">
                  Please enter a natural number value in the COUNT field.
                </p>
              ) : (
                <p></p>
              )}
              {requiredError ? (
                <p className="m-0 text-sm font-semibold text-tdplmaroon">
                  BATCH, COUNT, and MOVE DATE fields are required.
                </p>
              ) : (
                <p></p>
              )}
              {validationErrors.length > 0 && (
                <div className="mt-1">
                  {validationErrors.map((error, i) => (
                    <p key={i} className="m-0 text-sm font-semibold text-tdplmaroon">
                      {error.message}
                    </p>
                  ))}
                </div>
              )}
            </ModalBody>
            <InitialInfoButton isBack={isBack} onClose={onClose} />
            <AddGroup
              isOpen={addGroupDisclosure.isOpen}
              onOpenChange={() => {
                addGroupDisclosure.onOpenChange()
                handleAddGroupClose()
              }}
              showButton={false}
            />
          </>
        )}
      </ModalContent>
    </BaseModal>
  )
}
